/**
 * §5.3 versioned configuration contract. A lottery is a versioned JSON configuration interpreted by the rules
 * engine; nothing here requires a deploy. `configVersion` is an integer that is immutable once published —
 * any material change after the first entry creates a new version (§5.4).
 *
 * `validateLotteryConfig` returns structured issues for every impossible state in §5.4; the schema alone
 * only guarantees shape.
 */
import { z } from 'zod';
import { CountryCode, HexColor, HttpsUrl, IanaTimezone, IsoDateTime, LocaleTag, LocalizedText, Slug, Ulid, } from './common.js';
import { CountingMode, DRAW_ALGORITHM, EnergyLevel, EngagementTaskType, EntryMode, EntrySource, FulfillmentType, PrizeTier, SelectionMode, SocialPlatform, SponsorTier, } from './enums.js';
import { SponsorPlacement } from './events.js';
import { MinorAmount, SupportedCurrencyCode, SupportedMoneyDto } from './money.js';
import { validateSamplingCopy } from './copy.js';
// ---------------------------------------------------------------------------------------------------------
// Config version
// ---------------------------------------------------------------------------------------------------------
/** Integer, starts at 1, immutable once published; material changes after the first entry bump it (§5.4). */
export const ConfigVersion = z.number().int().min(1);
export const nextConfigVersion = (current) => current + 1;
/** Reference id used inside a configuration document for prizes/sponsors/rules (ULID of the catalogue row). */
export const RefId = Ulid;
// ---------------------------------------------------------------------------------------------------------
// Identity and market
// ---------------------------------------------------------------------------------------------------------
export const IdentityMarketSchema = z.object({
    slug: Slug,
    /** Market policy reference (legal entity, gates, age and residency rules) — tenant configuration row id. */
    marketPolicyRef: z.string().min(1).max(64),
    /** First locale is the default; every localized content map must cover it. */
    locales: z.array(LocaleTag).min(1),
    currency: SupportedCurrencyCode,
    timezone: IanaTimezone,
    termsVersion: z.string().min(1).max(32),
    privacyVersion: z.string().min(1).max(32),
    /** Where "help" goes: an in-app route or an https URL. */
    supportRoute: z.string().min(1).max(256),
});
// ---------------------------------------------------------------------------------------------------------
// Entry
// ---------------------------------------------------------------------------------------------------------
export const RequiredContact = z.enum(['WHATSAPP', 'SOCIAL_HANDLE', 'EITHER']);
export const CancellationPolicy = z.enum(['NO_CANCELLATION', 'CANCEL_BEFORE_CLOSE', 'CANCEL_WITHIN_WINDOW']);
export const RefundRule = z.enum(['FULL', 'FULL_MINUS_PROVIDER_FEES', 'NONE']);
export const EntryRulesSchema = z.object({
    mode: EntryMode,
    /** Minor units of the lottery currency; must be "0" for FREE and > 0 for PAID. */
    priceMinor: MinorAmount.default('0'),
    requiredContact: RequiredContact.default('EITHER'),
    /** Entries a participant may buy/claim in one submission. */
    eligibleQuantity: z.object({
        min: z.number().int().min(1).default(1),
        max: z.number().int().min(1).default(1),
    }),
    caps: z.object({
        /** Maximum confirmed entries per participant for the whole lottery (bonus entries excluded). */
        perPerson: z.number().int().min(1),
        perDay: z.number().int().min(1).optional(),
        /** Global cap on confirmed entries; also the "entry cap sells out" condition for fixed pools (§9.2). */
        global: z.number().int().min(1).optional(),
    }),
    /** D07: bonus entries are capped at ten per participant per lottery. */
    bonusEntryCap: z.number().int().min(0).max(10).default(10),
    cancellationPolicy: CancellationPolicy.default('NO_CANCELLATION'),
    cancellationWindowMinutes: z.number().int().min(1).optional(),
    refundRule: RefundRule.default('FULL'),
});
// ---------------------------------------------------------------------------------------------------------
// Eligibility
// ---------------------------------------------------------------------------------------------------------
export const EligibilityRulesSchema = z.object({
    minAge: z.number().int().min(0).max(120).default(18),
    geography: z
        .object({
        allow: z.array(CountryCode).default([]),
        deny: z.array(CountryCode).default([]),
    })
        .default({ allow: [], deny: [] }),
    residencyRequired: z.boolean().default(false),
    /** Free-text exclusion categories shown in the rules (e.g. "previous grand-prize winners"). */
    exclusions: z.array(z.string().max(200)).default([]),
    employeesExcluded: z.boolean().default(true),
    sponsorEmployeesExcluded: z.boolean().default(true),
    /** When identity assurance (external verification) is required at claim. */
    identityAssuranceTrigger: z
        .object({
        always: z.boolean().default(false),
        /** Prize value (minor units) at or above which verification is required. */
        prizeValueAtLeastMinor: MinorAmount.optional(),
        fulfillmentTypes: z.array(FulfillmentType).default(['CASH', 'PHYSICAL', 'IN_PERSON']),
    })
        .default({ always: false, fulfillmentTypes: ['CASH', 'PHYSICAL', 'IN_PERSON'] }),
});
// ---------------------------------------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------------------------------------
export const Recurrence = z.enum(['NONE', 'DAILY', 'WEEKLY', 'MONTHLY']);
/** §10.4: a payment confirming after close is excluded and refunded, never silently inserted. */
export const LatePaymentBehaviour = z.enum(['EXCLUDE_AND_REFUND']);
export const ScheduleRulesSchema = z.object({
    opensAt: IsoDateTime,
    closesAt: IsoDateTime,
    /** Required for scheduled mechanics (raffle, number pick); absent for instant / first-N. */
    drawAt: IsoDateTime.optional(),
    recurrence: Recurrence.default('NONE'),
    recurrenceCount: z.number().int().min(1).optional(),
    latePaymentBehaviour: LatePaymentBehaviour.default('EXCLUDE_AND_REFUND'),
    /** Defaults to the draw time (or close time for instant mechanics). */
    resultsPublishAt: IsoDateTime.optional(),
});
// ---------------------------------------------------------------------------------------------------------
// Outcome (mechanic blocks)
// ---------------------------------------------------------------------------------------------------------
/** One published prize tier in fixed tier order (§9.1 step 5). */
export const RaffleTierSchema = z.object({
    tier: PrizeTier,
    prizeId: RefId,
    /** Number of winners (prize units) allocated to this tier. */
    winners: z.number().int().min(1),
});
export const ScheduledRaffleBlockSchema = z.object({
    /** Fixed tier order; shuffled entries are mapped to tiers in this order. */
    tiers: z.array(RaffleTierSchema).min(1),
    /** May one entry win more than one tier? Default: a winning entry is removed from later tiers. */
    allowMultipleTierWins: z.boolean().default(false),
});
/** §9.2: N tickets and an exact vector of prize/loss outcomes. */
export const FixedPoolBlockSchema = z.object({
    poolSize: z.number().int().min(1),
    outcomes: z.array(z.object({ prizeId: RefId, count: z.number().int().min(1) })).min(1),
    /** What happens when a prize's stock is exhausted while tickets remain (§6.2): fail closed. */
    exhaustionBehaviour: z.enum(['PAUSE_NEW_OUTCOMES', 'CLOSE_POOL']).default('PAUSE_NEW_OUTCOMES'),
    /** Wheel/scratch presentation only: segment or card labels per outcome id (never a probability indicator). */
    segments: z.array(z.object({ label: z.string().max(40), prizeId: RefId.nullable() })).optional(),
});
export const QuickPickRule = z.enum(['ALLOWED', 'REQUIRED', 'DISABLED']);
export const MatchTierSchema = z.object({
    /** Exact number of matched numbers that wins this tier. */
    matches: z.number().int().min(1),
    prizeId: RefId,
    /** Fixed prize units available to this tier; winners beyond this are handled by `multipleWinnersPolicy`. */
    prizeUnits: z.number().int().min(1),
});
export const NumberPickBlockSchema = z.object({
    range: z.object({ min: z.number().int().min(0), max: z.number().int().min(1) }),
    pickCount: z.number().int().min(1),
    matchTiers: z.array(MatchTierSchema).min(1),
    quickPick: QuickPickRule.default('ALLOWED'),
    /** Published maximum liability (minor units) across all tiers; checked against the liability cap. */
    maxLiabilityMinor: MinorAmount,
    /** Treatment of multiple winning entries in one tier (§9.3): each wins a fixed unit until units run out, or the tier's units are shared. */
    multipleWinnersPolicy: z.enum(['EACH_FIXED_UNIT', 'SHARE_TIER_UNITS']).default('EACH_FIXED_UNIT'),
});
export const FirstNBlockSchema = z
    .object({
    countingMode: CountingMode,
    /** Explicit milestone positions and the prize for each. */
    milestones: z.array(z.object({ position: z.number().int().min(1), prizeId: RefId })).optional(),
    /** Every Nth qualified position wins `prizeId`, up to `limit` winners. */
    everyNth: z
        .object({ n: z.number().int().min(1), prizeId: RefId, limit: z.number().int().min(1) })
        .optional(),
})
    .refine((b) => (b.milestones?.length ?? 0) > 0 || b.everyNth !== undefined, {
    message: 'first-N needs milestones or an everyNth rule',
    path: ['milestones'],
});
const scheduledRaffleVariant = z.object({
    mechanic: z.literal('SCHEDULED_RAFFLE'),
    scheduledRaffle: ScheduledRaffleBlockSchema,
});
const instantWinVariant = z.object({ mechanic: z.literal('INSTANT_WIN'), fixedPool: FixedPoolBlockSchema });
const spinWheelVariant = z.object({ mechanic: z.literal('SPIN_WHEEL'), fixedPool: FixedPoolBlockSchema });
const scratchCardVariant = z.object({ mechanic: z.literal('SCRATCH_CARD'), fixedPool: FixedPoolBlockSchema });
const numberPickVariant = z.object({ mechanic: z.literal('NUMBER_PICK'), numberPick: NumberPickBlockSchema });
const firstNVariant = z.object({ mechanic: z.literal('FIRST_N'), firstN: FirstNBlockSchema });
/** The six "real" mechanics; FREE_GIVEAWAY wraps one of them. */
export const MainMechanicBlockSchema = z.discriminatedUnion('mechanic', [
    scheduledRaffleVariant,
    instantWinVariant,
    spinWheelVariant,
    scratchCardVariant,
    numberPickVariant,
    firstNVariant,
]);
export const OddsBasis = z.enum(['W_OF_N', 'ONE_IN_N', 'DEPENDS_ON_ENTRIES', 'FIXED_POSITIONS']);
export const OddsDisclosureSchema = z.object({
    basis: OddsBasis,
    /** Plain-language odds statement shown adjacent to the CTA (§4.2 A); localized. */
    statement: LocalizedText,
    /** For W_OF_N: winning tickets W and pool N as published (never updated after tickets are consumed, §9.2). */
    winningTickets: z.number().int().min(0).optional(),
    poolSize: z.number().int().min(1).optional(),
});
export const UnclaimedPrizePolicy = z.enum(['REDRAW', 'ROLLOVER', 'FORFEIT']);
export const RedrawPolicySchema = z.object({
    /** §2.4: one audited redraw after the claim window by default. */
    maxRedraws: z.number().int().min(0).default(1),
    requiresReason: z.literal(true).default(true),
    requiresNewSeedCommitment: z.literal(true).default(true),
    /** Second operator for high-value reveals (§3, §9.5). */
    dualOperatorAboveMinor: MinorAmount.optional(),
});
const outcomeCommon = {
    oddsDisclosure: OddsDisclosureSchema,
    redrawPolicy: RedrawPolicySchema.default({
        maxRedraws: 1,
        requiresReason: true,
        requiresNewSeedCommitment: true,
    }),
    algorithmVersion: z.literal(DRAW_ALGORITHM).default(DRAW_ALGORITHM),
};
export const OutcomeRulesSchema = z.discriminatedUnion('mechanic', [
    scheduledRaffleVariant.extend(outcomeCommon),
    instantWinVariant.extend(outcomeCommon),
    spinWheelVariant.extend(outcomeCommon),
    scratchCardVariant.extend(outcomeCommon),
    numberPickVariant.extend(outcomeCommon),
    firstNVariant.extend(outcomeCommon),
    z.object({ mechanic: z.literal('FREE_GIVEAWAY'), baseMechanic: MainMechanicBlockSchema, ...outcomeCommon }),
]);
/** Resolve the effective mechanic block (unwraps FREE_GIVEAWAY). */
export const effectiveMechanic = (outcome) => outcome.mechanic === 'FREE_GIVEAWAY' ? outcome.baseMechanic : outcome;
/** Mechanics whose outcome is decided at a scheduled draw (sampling converts after all main tiers, §8.1). */
export const SCHEDULED_MECHANICS = ['SCHEDULED_RAFFLE', 'NUMBER_PICK'];
export const isScheduledMechanic = (block) => SCHEDULED_MECHANICS.includes(block.mechanic);
// ---------------------------------------------------------------------------------------------------------
// Prizes and stock
// ---------------------------------------------------------------------------------------------------------
export const FulfillmentOwnerSchema = z.object({
    ownerId: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    backupOwnerId: z.string().max(64).optional(),
    dispatchSlaBusinessDays: z.number().int().min(0).optional(),
    escalationRoute: z.string().max(256).optional(),
});
export const SubstitutionPolicy = z.enum([
    'NONE',
    'EQUIVALENT_WITH_CONSENT',
    'EQUIVALENT_OR_HIGHER_WITH_CONSENT',
]);
export const PrizeConfigSchema = z.object({
    prizeId: RefId,
    name: LocalizedText,
    tier: PrizeTier,
    quantity: z.number().int().min(1),
    value: SupportedMoneyDto,
    fulfillmentType: FulfillmentType,
    /** D09: each prize names one owner (missing owner is a §5.4 publish blocker). */
    fulfillmentOwner: FulfillmentOwnerSchema.optional(),
    sponsorId: RefId.optional(),
    /** D10: Mode A fixed product (option axes) or Mode B value cap over approved brands. */
    selectionMode: SelectionMode.default('FIXED_PRODUCT'),
    productId: RefId.optional(),
    approvedBrandIds: z.array(RefId).default([]),
    valueCapMinor: MinorAmount.optional(),
    accentColor: HexColor.optional(),
    eligibleRegions: z.array(CountryCode).default([]),
    substitutionPolicy: SubstitutionPolicy.default('EQUIVALENT_WITH_CONSENT'),
});
export const PrizesStockSchema = z.object({
    prizes: z.array(PrizeConfigSchema).min(1),
    /** Maximum total prize liability in minor units (sum of quantity × value plus number-pick max liability). */
    prizeLiabilityCapMinor: MinorAmount.optional(),
    substitutionPolicy: SubstitutionPolicy.default('EQUIVALENT_WITH_CONSENT'),
});
// ---------------------------------------------------------------------------------------------------------
// Sponsors
// ---------------------------------------------------------------------------------------------------------
export const RevealSkin = z.enum(['NONE', 'WHEEL_HUB', 'SCRATCH_SKIN', 'DRAW_ROOM', 'FULL']);
export const SponsorConfigSchema = z.object({
    sponsorId: RefId,
    name: z.string().min(1).max(120),
    tier: SponsorTier,
    /** Weighted rotation within a tier (§7.1). */
    displayWeight: z.number().int().min(1).max(100).default(1),
    placements: z.array(SponsorPlacement).default([]),
    logoLightRef: z.string().max(512).optional(),
    logoDarkRef: z.string().max(512).optional(),
    brandColor: HexColor.optional(),
    tagline: LocalizedText.optional(),
    websiteUrl: HttpsUrl.optional(),
    ctaLabel: LocalizedText.optional(),
    ctaUrl: HttpsUrl.optional(),
    /** Prize-level sponsorship ("Sponsored by X" on that prize). */
    sponsoredPrizeIds: z.array(RefId).default([]),
    revealSkin: RevealSkin.default('NONE'),
});
export const SponsorsSchema = z.object({ sponsors: z.array(SponsorConfigSchema).default([]) });
// ---------------------------------------------------------------------------------------------------------
// Sampling (sponsor-default consolation, §8)
// ---------------------------------------------------------------------------------------------------------
export const SamplingEligibleRange = z.union([
    z.object({ firstN: z.number().int().min(1) }),
    z.object({ range: z.object({ start: z.number().int().min(1), end: z.number().int().min(1) }) }),
]);
/** Normalize `firstN` / `range` to inclusive positions. */
export const samplingPositions = (eligible) => 'firstN' in eligible
    ? { start: 1, end: eligible.firstN }
    : { start: eligible.range.start, end: eligible.range.end };
export const SamplingRuleSchema = z.object({
    ruleId: RefId,
    /** Rule version recorded on every award (§8.1 step 4). */
    version: z.number().int().min(1).default(1),
    enabled: z.boolean().default(true),
    sponsorId: RefId,
    defaultPrizeId: RefId,
    eligible: SamplingEligibleRange,
    /** D12: count unique qualified participants in first-qualified-entry order by default. */
    countingMode: CountingMode.default('UNIQUE_PARTICIPANT'),
    /** D12: free and paid qualify by default; paid-only requires prominent disclosure. */
    qualifyingEntryTypes: z.array(EntrySource).min(1).default(['FREE', 'PAID']),
    onePerParticipant: z.literal(true).default(true),
    /** Units reserved for this rule before publication; must cover the range (§8). */
    reservedUnits: z.number().int().min(0),
    /** Lower number = evaluated first (§8.1 step 3). */
    priority: z.number().int().min(1),
    /** Organizer-edited copy with {sponsor}/{product}/{mainPrize} variables. */
    copy: z.object({
        reveal: LocalizedText,
        claim: LocalizedText.optional(),
    }),
    mainWinnerExclusion: z.literal(true).default(true),
    /** Optional active window inside the schedule (default: whole lottery). */
    activeFrom: IsoDateTime.optional(),
    activeUntil: IsoDateTime.optional(),
});
export const SamplingRulesSchema = z.object({ rules: z.array(SamplingRuleSchema).default([]) });
// ---------------------------------------------------------------------------------------------------------
// Engagement
// ---------------------------------------------------------------------------------------------------------
/** §12.4 / D07 default bonus values. */
export const DEFAULT_TASK_BONUS = {
    REFERRAL: 2,
    SHARE: 1,
    FOLLOW: 1,
    LINK_CHANNEL: 1,
    DAILY_STREAK: 1,
};
export const DEFAULT_TOTAL_BONUS_CAP = 10;
export const PlatformConfigSchema = z.object({
    platform: SocialPlatform,
    /** The client's public account link (wa.me, instagram.com/..., t.me/...). */
    accountUrl: HttpsUrl,
    label: z.string().max(60).optional(),
    displayOrder: z.number().int().min(0),
});
export const EngagementTaskConfigSchema = z.object({
    taskId: RefId,
    type: EngagementTaskType,
    enabled: z.boolean().default(true),
    bonusEntries: z.number().int().min(0).max(10).optional(),
    /** Per-participant cap for this task (e.g. once per platform per lottery for FOLLOW / LINK_CHANNEL). */
    capPerParticipant: z.number().int().min(1).optional(),
    dailyCap: z.number().int().min(1).optional(),
    platform: SocialPlatform.optional(),
    destinationUrl: HttpsUrl.optional(),
    consentText: LocalizedText,
    title: LocalizedText,
});
export const EngagementSchema = z.object({
    platforms: z.array(PlatformConfigSchema).default([]),
    tasks: z.array(EngagementTaskConfigSchema).default([]),
    /** Total bonus-entry cap per participant per lottery (D07: ten). Mirrors entry.bonusEntryCap. */
    totalBonusCap: z.number().int().min(0).max(10).default(DEFAULT_TOTAL_BONUS_CAP),
});
/** Effective bonus for a task: configured value or the §12.4 default. */
export const taskBonusEntries = (task) => task.bonusEntries ?? DEFAULT_TASK_BONUS[task.type];
// ---------------------------------------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------------------------------------
/** §13.3 semantic colour roles. */
export const ThemeTokensSchema = z.object({
    canvas: HexColor,
    surface: HexColor,
    textPrimary: HexColor,
    textSecondary: HexColor,
    border: HexColor,
    action: HexColor,
    /** Text colour on the action colour. */
    actionText: HexColor,
    focus: HexColor,
    success: HexColor,
    warning: HexColor,
    error: HexColor,
    sponsorAccent: HexColor,
    prizeAccent: HexColor,
});
export const RevealStyle = z.enum([
    'DRAW_ROOM',
    'WHEEL',
    'SCRATCH',
    'INSTANT_CARD',
    'MILESTONE_COUNTER',
    'NUMBER_BOARD',
]);
export const HeroMediaSchema = z.object({
    kind: z.enum(['IMAGE', 'VIDEO']),
    ref: z.string().min(1).max(512),
    altText: z.string().min(1).max(300),
    posterRef: z.string().max(512).optional(),
    width: z.number().int().min(1).optional(),
    height: z.number().int().min(1).optional(),
});
export const LocalizedContentSchema = z.object({
    title: z.string().min(1).max(120),
    prizeStatement: z.string().min(1).max(300),
    howSelectionWorks: z.string().max(2000).optional(),
    sponsorStory: z.string().max(2000).optional(),
    faq: z.array(z.object({ q: z.string().max(300), a: z.string().max(2000) })).default([]),
    rulesRef: z.string().max(512).optional(),
    privacyRef: z.string().max(512).optional(),
});
export const PresentationSchema = z.object({
    theme: ThemeTokensSchema,
    /** Per-prize accent overrides (prizeId → colour). */
    prizeAccents: z.record(RefId, HexColor).default({}),
    heroMedia: HeroMediaSchema,
    revealStyle: RevealStyle,
    /** D14: full game-show energy with an always-available calm mode. */
    energyLevel: EnergyLevel.default('FULL'),
    soundDefault: z.literal(false).default(false),
    sponsorPlacements: z.array(SponsorPlacement).default([]),
    /** Localized content per locale; must cover the default locale. */
    content: z.record(LocaleTag, LocalizedContentSchema),
});
// ---------------------------------------------------------------------------------------------------------
// Claims
// ---------------------------------------------------------------------------------------------------------
export const ClaimField = z.enum([
    'LEGAL_NAME',
    'GOVERNMENT_ID',
    'ID_DOCUMENT_IMAGE',
    'WHATSAPP_NUMBER',
    'SOCIAL_HANDLE',
    'EMAIL',
    'DELIVERY_ADDRESS',
    'PICKUP_CHOICE',
    'PAYOUT_DESTINATION',
    'TAX_FIELDS',
    'DELIVERY_DESTINATION',
    'APPOINTMENT',
    'ADDITIONAL_DOCUMENTS',
]);
/** §11.1 defaults per fulfillment type. */
export const DEFAULT_REQUIRED_CLAIM_FIELDS = {
    CASH: ['LEGAL_NAME', 'GOVERNMENT_ID', 'ID_DOCUMENT_IMAGE', 'PAYOUT_DESTINATION'],
    PHYSICAL: ['LEGAL_NAME', 'GOVERNMENT_ID', 'ID_DOCUMENT_IMAGE', 'EMAIL', 'DELIVERY_ADDRESS'],
    DIGITAL: ['LEGAL_NAME', 'DELIVERY_DESTINATION'],
    IN_PERSON: ['LEGAL_NAME', 'GOVERNMENT_ID', 'ID_DOCUMENT_IMAGE', 'APPOINTMENT', 'ADDITIONAL_DOCUMENTS'],
};
export const RetentionClass = z.enum(['STANDARD_90D', 'EXTENDED_LEGAL_HOLD', 'MINIMAL_30D']);
export const ClaimsRulesSchema = z.object({
    /** §2.4: fourteen calendar days from winner notification, in the lottery timezone. */
    claimWindowDays: z.number().int().min(0).default(14),
    /** §2.4 / §6.2: fifteen-minute variant hold with one automatic extension while verification waits on the platform. */
    variantHoldMinutes: z.number().int().min(1).default(15),
    holdAutoExtensions: z.number().int().min(0).default(1),
    requiredFields: z
        .object({
        CASH: z.array(ClaimField),
        PHYSICAL: z.array(ClaimField),
        DIGITAL: z.array(ClaimField),
        IN_PERSON: z.array(ClaimField),
    })
        .default(DEFAULT_REQUIRED_CLAIM_FIELDS),
    /** VerificationProvider adapter name (D08); `mock` until the client names its solution. */
    verificationAdapter: z.string().min(1).max(64).default('mock'),
    retentionClass: RetentionClass.default('STANDARD_90D'),
    unclaimedPrizePolicy: UnclaimedPrizePolicy.default('REDRAW'),
    /** Appeal window after a rejection before the unclaimed policy applies (§11.2). */
    appealWindowDays: z.number().int().min(0).default(7),
    supportSla: z
        .object({
        firstResponseMinutesLive: z.number().int().min(1).default(15),
        firstResponseBusinessHours: z.number().int().min(1).default(4),
        reviewBusinessDays: z.number().int().min(1).default(1),
    })
        .default({ firstResponseMinutesLive: 15, firstResponseBusinessHours: 4, reviewBusinessDays: 1 }),
});
// ---------------------------------------------------------------------------------------------------------
// Whole configuration
// ---------------------------------------------------------------------------------------------------------
export const LotteryConfigSchema = z.object({
    configVersion: ConfigVersion,
    identity: IdentityMarketSchema,
    entry: EntryRulesSchema,
    eligibility: EligibilityRulesSchema,
    schedule: ScheduleRulesSchema,
    outcome: OutcomeRulesSchema,
    prizes: PrizesStockSchema,
    sponsors: SponsorsSchema.default({ sponsors: [] }),
    sampling: SamplingRulesSchema.default({ rules: [] }),
    engagement: EngagementSchema.default({ platforms: [], tasks: [], totalBonusCap: DEFAULT_TOTAL_BONUS_CAP }),
    presentation: PresentationSchema,
    claims: ClaimsRulesSchema,
});
// ---------------------------------------------------------------------------------------------------------
// WCAG contrast (§13.3, Appendix A "Sponsor asset fails contrast")
// ---------------------------------------------------------------------------------------------------------
const channel = (hex, offset) => {
    const v = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};
/** WCAG 2.x relative luminance of an `#rrggbb` colour. */
export const relativeLuminance = (hex) => {
    const h = hex.replace('#', '');
    return 0.2126 * channel(h, 0) + 0.7152 * channel(h, 2) + 0.0722 * channel(h, 4);
};
/** WCAG contrast ratio between two colours (≥ 1, order-independent). */
export const contrastRatio = (a, b) => {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const [light, dark] = la >= lb ? [la, lb] : [lb, la];
    return (light + 0.05) / (dark + 0.05);
};
export const CONTRAST_NORMAL_TEXT = 4.5;
export const CONTRAST_ESSENTIAL_UI = 3;
/** Critical token pairs: text ≥ 4.5:1; essential UI ≥ 3:1. */
export const CRITICAL_CONTRAST_PAIRS = [
    { fg: 'textPrimary', bg: 'canvas', min: CONTRAST_NORMAL_TEXT },
    { fg: 'textPrimary', bg: 'surface', min: CONTRAST_NORMAL_TEXT },
    { fg: 'textSecondary', bg: 'canvas', min: CONTRAST_NORMAL_TEXT },
    { fg: 'textSecondary', bg: 'surface', min: CONTRAST_NORMAL_TEXT },
    { fg: 'actionText', bg: 'action', min: CONTRAST_NORMAL_TEXT },
    { fg: 'error', bg: 'canvas', min: CONTRAST_NORMAL_TEXT },
    { fg: 'focus', bg: 'canvas', min: CONTRAST_ESSENTIAL_UI },
    { fg: 'action', bg: 'canvas', min: CONTRAST_ESSENTIAL_UI },
];
// ---------------------------------------------------------------------------------------------------------
// §5.4 validation
// ---------------------------------------------------------------------------------------------------------
export const ConfigIssueCode = z.enum([
    'schema',
    'price_mode_mismatch',
    'paid_without_provider',
    'free_giveaway_not_free',
    'close_before_open',
    'draw_before_close',
    'draw_missing',
    'results_before_draw',
    'zero_claim_window',
    'liability_negative',
    'liability_exceeds_cap',
    'guaranteed_exceeds_stock',
    'guaranteed_exceeds_available',
    'pool_outcomes_exceed_pool',
    'number_pick_range_invalid',
    'number_pick_tier_invalid',
    'number_pick_liability_understated',
    'first_n_invalid',
    'unknown_prize_ref',
    'unknown_sponsor_ref',
    'missing_fulfillment_owner',
    'value_cap_without_brands',
    'value_cap_without_cap',
    'inaccessible_theme_token',
    'sampling_range_invalid',
    'sampling_range_exceeds_reserved',
    'sampling_reserved_exceeds_stock',
    'sampling_copy_invalid',
    'sampling_duplicate_priority',
    'bonus_cap_exceeded',
    'bonus_cap_mismatch',
    'platform_duplicate',
    'locale_content_missing',
    'algorithm_version',
    'min_age_below_default',
    'odds_disclosure_mismatch',
]);
const sumBig = (values) => values.reduce((a, b) => a + b, 0n);
/** Guaranteed prize units per prizeId implied by the outcome block (what must exist in stock). */
export const guaranteedUnitsByPrize = (outcome) => {
    const block = effectiveMechanic(outcome);
    const units = new Map();
    const add = (prizeId, n) => units.set(prizeId, (units.get(prizeId) ?? 0) + n);
    switch (block.mechanic) {
        case 'SCHEDULED_RAFFLE':
            for (const t of block.scheduledRaffle.tiers)
                add(t.prizeId, t.winners);
            break;
        case 'INSTANT_WIN':
        case 'SPIN_WHEEL':
        case 'SCRATCH_CARD':
            for (const o of block.fixedPool.outcomes)
                add(o.prizeId, o.count);
            break;
        case 'NUMBER_PICK':
            for (const t of block.numberPick.matchTiers)
                add(t.prizeId, t.prizeUnits);
            break;
        case 'FIRST_N':
            for (const m of block.firstN.milestones ?? [])
                add(m.prizeId, 1);
            if (block.firstN.everyNth)
                add(block.firstN.everyNth.prizeId, block.firstN.everyNth.limit);
            break;
    }
    return units;
};
/** Total liability: Σ quantity × value over prizes, plus the published number-pick maximum liability. */
export const totalLiabilityMinor = (config) => {
    const prizeTotal = sumBig(config.prizes.prizes.map((p) => BigInt(p.quantity) * BigInt(p.value.amountMinor)));
    const block = effectiveMechanic(config.outcome);
    const numberPick = block.mechanic === 'NUMBER_PICK' ? BigInt(block.numberPick.maxLiabilityMinor) : 0n;
    return prizeTotal + numberPick;
};
/** Validate an already-parsed configuration against the §5.4 impossible states. */
export const validateLotteryConfig = (config, ctx) => {
    const issues = [];
    const error = (path, code, message) => issues.push({ path, code, message, severity: 'error' });
    const warning = (path, code, message) => issues.push({ path, code, message, severity: 'warning' });
    const prizeById = new Map(config.prizes.prizes.map((p) => [p.prizeId, p]));
    const sponsorIds = new Set(config.sponsors.sponsors.map((s) => s.sponsorId));
    const block = effectiveMechanic(config.outcome);
    // --- entry / payment --------------------------------------------------------------------------------
    const price = BigInt(config.entry.priceMinor);
    if (config.entry.mode === 'PAID' && price <= 0n)
        error('entry.priceMinor', 'price_mode_mismatch', 'paid entry needs a price greater than zero');
    if (config.entry.mode === 'FREE' && price !== 0n)
        error('entry.priceMinor', 'price_mode_mismatch', 'free entry must have a zero price');
    if (config.entry.mode === 'PAID' && !ctx.paymentProviderConfigured)
        error('entry.mode', 'paid_without_provider', 'paid entry requires a configured payment provider (D03)');
    if (config.outcome.mechanic === 'FREE_GIVEAWAY' && (config.entry.mode !== 'FREE' || price !== 0n))
        error('outcome.mechanic', 'free_giveaway_not_free', 'a free giveaway must use FREE entry at zero price');
    if (config.entry.eligibleQuantity.max < config.entry.eligibleQuantity.min)
        error('entry.eligibleQuantity.max', 'schema', 'max quantity below min quantity');
    // --- schedule ---------------------------------------------------------------------------------------
    const opens = Date.parse(config.schedule.opensAt);
    const closes = Date.parse(config.schedule.closesAt);
    if (!(closes > opens))
        error('schedule.closesAt', 'close_before_open', 'close must be after open');
    const scheduled = isScheduledMechanic(block);
    if (config.schedule.drawAt) {
        const draw = Date.parse(config.schedule.drawAt);
        if (draw < closes)
            error('schedule.drawAt', 'draw_before_close', 'draw cannot happen before close');
        if (config.schedule.resultsPublishAt && Date.parse(config.schedule.resultsPublishAt) < draw)
            error('schedule.resultsPublishAt', 'results_before_draw', 'results cannot publish before the draw');
    }
    else if (scheduled) {
        error('schedule.drawAt', 'draw_missing', `${block.mechanic} needs a draw time`);
    }
    // --- claims -----------------------------------------------------------------------------------------
    if (config.claims.claimWindowDays < 1)
        error('claims.claimWindowDays', 'zero_claim_window', 'claim window must be at least one day');
    // --- prizes -----------------------------------------------------------------------------------------
    config.prizes.prizes.forEach((p, i) => {
        const base = `prizes.prizes[${i}]`;
        if (!p.fulfillmentOwner || !p.fulfillmentOwner.ownerId)
            error(`${base}.fulfillmentOwner`, 'missing_fulfillment_owner', 'every prize names one fulfillment owner (D09)');
        if (p.selectionMode === 'VALUE_CAP') {
            if (p.approvedBrandIds.length === 0)
                error(`${base}.approvedBrandIds`, 'value_cap_without_brands', 'value-cap mode needs at least one approved brand');
            if (!p.valueCapMinor || BigInt(p.valueCapMinor) <= 0n)
                error(`${base}.valueCapMinor`, 'value_cap_without_cap', 'value-cap mode needs a positive value cap');
        }
        if (p.sponsorId && !sponsorIds.has(p.sponsorId))
            error(`${base}.sponsorId`, 'unknown_sponsor_ref', `sponsor ${p.sponsorId} is not configured`);
        if (p.value.currency !== config.identity.currency)
            error(`${base}.value.currency`, 'schema', 'prize value must be in the lottery currency (D01)');
    });
    // --- liability --------------------------------------------------------------------------------------
    const liability = totalLiabilityMinor(config);
    if (liability < 0n)
        error('prizes', 'liability_negative', 'liability cannot be negative');
    if (config.prizes.prizeLiabilityCapMinor !== undefined &&
        liability > BigInt(config.prizes.prizeLiabilityCapMinor))
        error('prizes.prizeLiabilityCapMinor', 'liability_exceeds_cap', `total liability ${liability} exceeds cap ${config.prizes.prizeLiabilityCapMinor}`);
    // --- outcome ↔ stock --------------------------------------------------------------------------------
    const guaranteed = guaranteedUnitsByPrize(config.outcome);
    const reservedByPrize = new Map();
    for (const r of config.sampling.rules) {
        if (!r.enabled)
            continue;
        reservedByPrize.set(r.defaultPrizeId, (reservedByPrize.get(r.defaultPrizeId) ?? 0) + r.reservedUnits);
    }
    for (const [prizeId, units] of guaranteed) {
        const prize = prizeById.get(prizeId);
        if (!prize) {
            error('outcome', 'unknown_prize_ref', `prize ${prizeId} referenced by the outcome is not configured`);
            continue;
        }
        const reserved = reservedByPrize.get(prizeId) ?? 0;
        if (units + reserved > prize.quantity)
            error('outcome', 'guaranteed_exceeds_stock', `prize ${prizeId}: ${units} guaranteed + ${reserved} reserved exceeds quantity ${prize.quantity}`);
        const available = ctx.availableStock?.[prizeId];
        if (available !== undefined && units + reserved > available)
            error('outcome', 'guaranteed_exceeds_available', `prize ${prizeId}: only ${available} units available`);
    }
    switch (block.mechanic) {
        case 'INSTANT_WIN':
        case 'SPIN_WHEEL':
        case 'SCRATCH_CARD': {
            const total = block.fixedPool.outcomes.reduce((a, o) => a + o.count, 0);
            if (total > block.fixedPool.poolSize)
                error('outcome.fixedPool.outcomes', 'pool_outcomes_exceed_pool', `${total} prize tickets exceed pool size ${block.fixedPool.poolSize}`);
            const odds = config.outcome.oddsDisclosure;
            if (odds.basis === 'W_OF_N' &&
                (odds.winningTickets !== total || odds.poolSize !== block.fixedPool.poolSize))
                error('outcome.oddsDisclosure', 'odds_disclosure_mismatch', 'published W/N must equal the pool vector');
            break;
        }
        case 'NUMBER_PICK': {
            const np = block.numberPick;
            const size = np.range.max - np.range.min + 1;
            if (size < 2)
                error('outcome.numberPick.range', 'number_pick_range_invalid', 'range must contain at least two numbers');
            if (np.pickCount > size)
                error('outcome.numberPick.pickCount', 'number_pick_range_invalid', 'pick count exceeds the range size');
            const seen = new Set();
            np.matchTiers.forEach((t, i) => {
                if (t.matches > np.pickCount)
                    error(`outcome.numberPick.matchTiers[${i}].matches`, 'number_pick_tier_invalid', 'matches exceed pick count');
                if (seen.has(t.matches))
                    error(`outcome.numberPick.matchTiers[${i}].matches`, 'number_pick_tier_invalid', 'duplicate match tier');
                seen.add(t.matches);
            });
            break;
        }
        case 'FIRST_N': {
            const fn = block.firstN;
            const positions = new Set();
            (fn.milestones ?? []).forEach((m, i) => {
                if (positions.has(m.position))
                    error(`outcome.firstN.milestones[${i}].position`, 'first_n_invalid', 'duplicate milestone position');
                positions.add(m.position);
            });
            break;
        }
        default:
            break;
    }
    if (config.outcome.algorithmVersion !== DRAW_ALGORITHM)
        error('outcome.algorithmVersion', 'algorithm_version', `expected ${DRAW_ALGORITHM}`);
    // --- sampling (§8, D12) -----------------------------------------------------------------------------
    const priorities = new Set();
    config.sampling.rules.forEach((r, i) => {
        const base = `sampling.rules[${i}]`;
        if (!r.enabled)
            return;
        const { start, end } = samplingPositions(r.eligible);
        if (start < 1 || end < start)
            error(`${base}.eligible`, 'sampling_range_invalid', `range ${start}–${end} is not a valid participant range`);
        const size = Math.max(0, end - start + 1);
        if (size > r.reservedUnits)
            error(`${base}.reservedUnits`, 'sampling_range_exceeds_reserved', `range covers ${size} positions but only ${r.reservedUnits} units are reserved`);
        const prize = prizeById.get(r.defaultPrizeId);
        if (!prize)
            error(`${base}.defaultPrizeId`, 'unknown_prize_ref', `prize ${r.defaultPrizeId} is not configured`);
        else if ((reservedByPrize.get(r.defaultPrizeId) ?? 0) + (guaranteed.get(r.defaultPrizeId) ?? 0) >
            prize.quantity)
            error(`${base}.reservedUnits`, 'sampling_reserved_exceeds_stock', `reserved units exceed quantity of prize ${r.defaultPrizeId}`);
        if (!sponsorIds.has(r.sponsorId))
            error(`${base}.sponsorId`, 'unknown_sponsor_ref', `sponsor ${r.sponsorId} is not configured`);
        if (priorities.has(r.priority))
            error(`${base}.priority`, 'sampling_duplicate_priority', 'sampling priorities must be unique');
        priorities.add(r.priority);
        for (const [locale, text] of Object.entries(r.copy.reveal)) {
            for (const problem of validateSamplingCopy(text))
                error(`${base}.copy.reveal.${locale}`, 'sampling_copy_invalid', problem);
        }
    });
    // --- engagement (§12.4) -----------------------------------------------------------------------------
    if (config.engagement.totalBonusCap !== config.entry.bonusEntryCap)
        error('engagement.totalBonusCap', 'bonus_cap_mismatch', 'engagement.totalBonusCap must equal entry.bonusEntryCap');
    config.engagement.tasks.forEach((t, i) => {
        if (taskBonusEntries(t) > config.engagement.totalBonusCap)
            error(`engagement.tasks[${i}].bonusEntries`, 'bonus_cap_exceeded', 'a single task cannot exceed the total bonus cap');
    });
    const platforms = new Set();
    config.engagement.platforms.forEach((p, i) => {
        if (platforms.has(p.platform))
            error(`engagement.platforms[${i}].platform`, 'platform_duplicate', 'platform listed twice');
        platforms.add(p.platform);
    });
    // --- presentation (§13.3) ---------------------------------------------------------------------------
    for (const pair of CRITICAL_CONTRAST_PAIRS) {
        const ratio = contrastRatio(config.presentation.theme[pair.fg], config.presentation.theme[pair.bg]);
        if (ratio < pair.min)
            error(`presentation.theme.${pair.fg}`, 'inaccessible_theme_token', `${pair.fg} on ${pair.bg} is ${ratio.toFixed(2)}:1, below ${pair.min}:1`);
    }
    for (const [prizeId, colour] of Object.entries(config.presentation.prizeAccents)) {
        const ratio = contrastRatio(colour, config.presentation.theme.canvas);
        if (ratio < CONTRAST_ESSENTIAL_UI)
            warning(`presentation.prizeAccents.${prizeId}`, 'inaccessible_theme_token', `prize accent is ${ratio.toFixed(2)}:1 on canvas; neutral fallback will be used`);
    }
    const defaultLocale = config.identity.locales[0];
    if (defaultLocale && !config.presentation.content[defaultLocale])
        error(`presentation.content.${defaultLocale}`, 'locale_content_missing', 'content for the default locale is required');
    // --- eligibility ------------------------------------------------------------------------------------
    if (config.eligibility.minAge < 18)
        warning('eligibility.minAge', 'min_age_below_default', 'minimum age is below the 18+ default; confirm the market policy allows it');
    return { ok: !issues.some((i) => i.severity === 'error'), issues, liabilityMinor: liability.toString() };
};
/** Parse + validate in one step; schema failures are reported as `schema` issues with zod paths. */
export const parseAndValidateLotteryConfig = (input, ctx) => {
    const parsed = LotteryConfigSchema.safeParse(input);
    if (!parsed.success) {
        return {
            ok: false,
            liabilityMinor: '0',
            issues: parsed.error.issues.map((i) => ({
                path: i.path
                    .map((p) => (typeof p === 'number' ? `[${p}]` : String(p)))
                    .join('.')
                    .replace(/\.\[/g, '['),
                code: 'schema',
                message: i.message,
                severity: 'error',
            })),
        };
    }
    return { ...validateLotteryConfig(parsed.data, ctx), config: parsed.data };
};
//# sourceMappingURL=lottery-config.js.map